import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import {
    FormControl,
    Icon,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from '@mui/material'
import {
    ReactElement,
    useEffect,
    useMemo,
    useState,
    useRef,
    useCallback,
} from 'react'
import {
    getUserDataByEmail,
    inviteUser,
    updateUser,
} from '../../services/userServices'
import { IAddUser, IAddUserMandatoryFields } from './interfaces'
import { useAppSelector } from '../../redux/hook'
import { CloseOutlined } from '@mui/icons-material'
import { useUnits } from '../../hooks/useUnits'
import UnitsPreferences from '../Layout/Navbar/UnitsPreferences'
import {
    defaultAddUserFormData,
    MODE,
    UserPermissions,
    USER_ROLES,
    RootOrgName,
} from '../../constants/constants'
import LocalizationPreferences from '../Layout/Navbar/LocalizationPreferences'
import { defaultLocalization } from '../../utils/DateAndTimeUtils'
import { useLocalization } from '../../hooks/useLocalization'
import { useTranslation } from 'react-i18next'
import PaperModalComponent from '../Common/PaperModalComponent'
import { getRoleAuthId } from '../../utils/UserManagementUtils'
import { usePermissions } from '../../hooks/usePermissions'
import MultiEmailField from '../Common/MultiEmailField'
import { checkInviteEmails } from '../../services/bulkInvitationService'
import { BLOCKING_VERDICTS, MAX_USERS_PER_BATCH } from './constants'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import ConfirmationDialog from '../Common/ConfirmationDialog'
import BulkSendWarning from './BulkInvite/BulkSendWarning'

const adminAboveRoles: string[] = [
    USER_ROLES.SYSTEM_ADMIN,
    USER_ROLES.SYSTEM_READER,
    USER_ROLES.DEVICE_MANAGER,
]

export default function AddUser(props: Readonly<IAddUser>): ReactElement {
    const { t } = useTranslation()
    const {
        openAddOrEditUser,
        handleAddOrEditUser,
        readOnly: readOnlyProps,
        onBatchStarted,
    } = props

    const org = useAppSelector((state) => state.org.org)
    const permissions = useAppSelector((state) => state.org.permissions)
    const isSuperAdmin = permissions?.includes(UserPermissions.ORG_ADMIN)

    const { open, mode, formData: data } = openAddOrEditUser
    const { totalUsers } = data
    const role = mode === MODE.ADD && totalUsers === 0 ? 'Admin' : data.role
    const [formData, setFormData] = useState({ ...data, role })
    const [errors, setErrors] = useState<Partial<IAddUserMandatoryFields>>({})
    const [isUpdated, setIsUpdated] = useState<boolean>(true)
    const [isFetchingUser, setIsFetchingUser] = useState<boolean>(false)
    const lastFetchedEmail = useRef<string>('')
    // UBQ-5212: ADD mode invites several people at once. Kept separate from formData.email rather than
    // widening it to string[], so EDIT — always one existing user — is untouched.
    const [emails, setEmails] = useState<string[]>([])
    // Address (lower-cased) -> server verdict, so someone is told an address is already a member while
    // they are still typing rather than after they submit.
    const [verdicts, setVerdicts] = useState<Record<string, string>>({})
    const [sendInvitation, setSendInvitation] = useState<boolean>(true)
    // UBQ-5212: batch sends are confirmed before they go out; see handleSubmit.
    const [confirmBulkOpen, setConfirmBulkOpen] = useState<boolean>(false)
    const roles = useAppSelector((state) => state.roles.roles)
    const unitsList = useAppSelector((state) => state.user.unitsList)
    const { hasPermission } = usePermissions()
    //if user admin opens system admin, system reader or device manager. we need to show in readOnly mode
    const readOnly = useMemo(() => {
        if (
            hasPermission([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_ADMIN,
            ])
        ) {
            return readOnlyProps
        } else if (
            hasPermission(UserPermissions.USER_ADMIN) &&
            adminAboveRoles.includes(formData.role)
        ) {
            return true
        } else {
            return readOnlyProps
        }
    }, [readOnlyProps, formData.role, hasPermission])
    const userRolesList: { role: string; disabled: boolean }[] = useMemo(
        () => [
            { role: USER_ROLES.ADMIN, disabled: false },
            { role: USER_ROLES.WRITER, disabled: false },
            { role: USER_ROLES.READER, disabled: false },
            { role: USER_ROLES.ANALYST, disabled: false },
            { role: USER_ROLES.DEVICE_MANAGER, disabled: !isSuperAdmin },
            {
                role: USER_ROLES.SYSTEM_ADMIN,
                disabled: !isSuperAdmin || org?.name !== RootOrgName,
            },
            {
                role: USER_ROLES.SYSTEM_READER,
                disabled: !isSuperAdmin || org?.name !== RootOrgName,
            },
        ],
        [isSuperAdmin, org],
    )

    const initialUnits = useMemo(() => {
        const orgUnits = org?.orgUnits
        const userUnits = data?.units
        return (
            unitsList?.map((i) => ({
                ...i,
                selected:
                    userUnits?.[i?.id] ??
                    orgUnits?.units?.[i?.id] ??
                    i?.units?.[0],
            })) || []
        )
    }, [unitsList, org?.orgUnits, data?.units])

    const titleText = useMemo(() => {
        let title = ''

        if (mode === MODE.EDIT) {
            if (readOnly) {
                title = t('viewUser')
            } else {
                title = t('editUser')
            }
        } else {
            title = t('addUser')
        }
        return title
    }, [mode, readOnly, t])

    const { units, onChangeUnits, isUnitsUpdated, setUnits } =
        useUnits(initialUnits)

    // set initial localization values
    const initialValues = useMemo(() => {
        let localizationObj = defaultLocalization
        if (mode === MODE.EDIT && formData?.localization) {
            localizationObj = {
                ...formData.localization,
                language:
                    formData.localization?.language ??
                    org?.preferences?.localization?.language ??
                    'en',
            }
        } else if (org?.preferences?.localization) {
            localizationObj = {
                ...org.preferences.localization,
                language: org?.preferences.localization.language ?? 'en',
            }
        }

        // Ensure language property exists
        return {
            dateFormat: localizationObj.dateFormat,
            timeFormat: localizationObj.timeFormat,
            timeZone: localizationObj.timeZone,
            language: localizationObj.language ?? 'en',
        }
    }, [mode, formData, org])

    // localization hook
    const { localization, handleLocalizationChange, isLocalizationUpdated } =
        useLocalization(initialValues)

    useEffect(() => {
        if (role === formData.role && mode !== MODE.ADD) {
            setIsUpdated(false)
        } else {
            setIsUpdated(true)
        }
    }, [formData.role, mode, role])

    const handleChange = useCallback((event: SelectChangeEvent) => {
        setFormData((prev) => ({ ...prev, role: event.target.value }))
    }, [])

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target
            setFormData((prev) => ({ ...prev, [name]: value }))
        },
        [],
    )

    const clearForm = useCallback(() => {
        setFormData({
            ...defaultAddUserFormData,
        })
        setErrors({})
        setEmails([])
        setVerdicts({})
        setSendInvitation(true)
        setConfirmBulkOpen(false)
    }, [])

    const fetchUserData = useCallback(async () => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        const currentEmail = formData.email?.trim()

        if (
            currentEmail &&
            emailRegex.test(currentEmail) &&
            currentEmail !== lastFetchedEmail.current
        ) {
            lastFetchedEmail.current = currentEmail
            setIsFetchingUser(true)
            const userData = await getUserDataByEmail(
                org?.id || '',
                currentEmail,
            )
            if (userData?.preferences?.units) {
                const userUnits = userData.preferences.units
                const updatedUnits =
                    unitsList?.map((i) => ({
                        ...i,
                        selected: userUnits?.[i?.id] ?? '',
                    })) || []
                setUnits(updatedUnits)
            }
            setIsFetchingUser(false)
        }
    }, [formData.email, unitsList, setUnits, org?.id])

    const handleEmailBlur = useCallback(async () => {
        await fetchUserData()
    }, [fetchUserData])

    /**
     * UBQ-5212: ask the server which of the typed addresses can actually be invited.
     *
     * Debounced because it fires on every chip added, and guarded against a stale response overwriting a
     * newer one — the same ignore-stale pattern the org picker needed.
     */
    useEffect(() => {
        if (mode !== MODE.ADD || !org?.id) {
            return
        }
        // Only the addresses we have no verdict for. Pasting 10 and then 10 more asks about the second
        // 10, not all 20 — and re-running for an unrelated reason asks about nothing at all. Verdicts
        // are keyed on the lower-cased address, which is what the server returns.
        const unchecked = emails.filter(
            (email) => !(email.toLowerCase() in verdicts),
        )
        if (unchecked.length === 0) {
            return
        }
        let cancelled = false
        const timer = setTimeout(() => {
            checkInviteEmails(org.id, unchecked)
                // Merged, not replaced: the response only covers the new addresses, so overwriting
                // would drop the verdicts already shown against the earlier chips.
                .then((result) => {
                    if (!cancelled) {
                        setVerdicts((previous) => ({ ...previous, ...result }))
                    }
                })
                // A failed check must not block the form: the server validates again on submit and
                // reports anything unusable in the response.
                .catch(() => undefined)
        }, 400)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [emails, mode, org?.id, verdicts])

    // Verdicts are per account — "already a member" is only true of the org that was asked. Switching
    // accounts with the dialog open would otherwise keep showing the previous org's answers.
    useEffect(() => {
        setVerdicts({})
    }, [org?.id])

    // Prefilling units from an existing user only makes sense for a single address; with several there is
    // no one user to read from. Replaces the search adornment the field used to carry.
    useEffect(() => {
        if (mode === MODE.ADD && emails.length === 1) {
            fetchUserData()
        }
    }, [emails, mode, fetchUserData])

    /** Addresses the server says cannot be invited. Submit stays disabled until they are removed. */
    const blockedEmails = useMemo(
        () =>
            emails.filter((email) =>
                BLOCKING_VERDICTS.includes(verdicts[email.toLowerCase()] ?? ''),
            ),
        [emails, verdicts],
    )

    const handleEmailsChange = useCallback((list: string[]) => {
        setEmails(list)
        // Keep formData.email in step for the single-address path, which still posts it.
        setFormData((prev) => ({
            ...prev,
            email: list.length === 1 ? list[0] : '',
        }))
        setErrors((prev) => ({ ...prev, email: undefined }))
    }, [])

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault()
                fetchUserData()
            }
        },
        [fetchUserData],
    )

    const validateForm = useCallback(() => {
        let isValid = true
        const errors: Partial<IAddUserMandatoryFields> = {}

        if (mode === MODE.ADD) {
            // UBQ-5212: the field itself rejects anything malformed, so what is left to check is that
            // there is at least one address, that none of them is already in the account, and that the
            // batch is inside the server's cap — stated here so it fails in the form, not after submit.
            if (emails.length === 0) {
                errors.email = t('emailRequired')
                isValid = false
            } else if (blockedEmails.length > 0) {
                errors.email = t('inviteBlockedAddresses', {
                    emails: blockedEmails.join(', '),
                })
                isValid = false
            } else if (emails.length > MAX_USERS_PER_BATCH) {
                errors.email = t('inviteTooManyAddresses', {
                    count: emails.length,
                    max: MAX_USERS_PER_BATCH,
                })
                isValid = false
            }
        } else {
            const emailRegex =
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            const isValidEmail = emailRegex.test(formData.email)
            if (!formData.email) {
                errors.email = t('emailRequired')
                isValid = false
            } else if (!isValidEmail) {
                errors.email = t('emailInvalid')
                isValid = false
            }
        }

        if (!formData.role) {
            errors.role = t('roleRequired')
            isValid = false
        }

        setErrors(errors)
        return isValid
    }, [formData.email, formData.role, mode, emails, blockedEmails, t])

    const submitForm = useCallback(async () => {
        const updatedUints: { [id: string]: string } = {}
        units?.forEach((i) => {
            updatedUints[i.id] = i.selected || ''
        })
        const role = getRoleAuthId(roles, formData.role)
        if (mode === MODE.ADD) {
            // UBQ-5212: one call whether it is one address or two hundred. The server sends a single
            // invite inline — so its own error message still reaches the user (UBQ-5306) — and hands a
            // longer list to a background worker that paces the sends.
            const response = await inviteUser(
                {
                    emails,
                    roleAuth0Id: role,
                    sendInvitation,
                    preferences: {
                        units: updatedUints,
                        localization,
                    },
                },
                org?.id || '',
            )
            /* UBQ-5486: a batch is still sending when this returns, so the users are not in the listing
               yet. Report it and close without refreshing — the parent refreshes once it finishes.
               Refreshing here is what showed one user of thirty. The single-address case has no batchId
               and falls through to the refresh below, unchanged. */
            if (response?.batchId && response.startedAt && onBatchStarted) {
                onBatchStarted(
                    response.batchId,
                    response.startedAt,
                    sendInvitation,
                )
                clearForm()
                handleAddOrEditUser(false)
                return
            }
        } else {
            const payload = {
                email: formData.email,
                roleAuth0Id: getRoleAuthId(roles, formData.role),
                id: formData.id,
                userorgId: formData.id,
                preferences: {
                    units: updatedUints,
                    localization,
                },
            }
            await updateUser(payload, org?.id || '')
        }

        clearForm()
        handleAddOrEditUser(true)
    }, [
        units,
        roles,
        formData.role,
        formData.email,
        formData.id,
        mode,
        org?.id,
        localization,
        handleAddOrEditUser,
        clearForm,
        emails,
        sendInvitation,
        onBatchStarted,
        t,
    ])

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (!validateForm()) return
            /* UBQ-5212: a batch is confirmed first, with the counts and the
               deliverability warning the ticket mandates. Previously a 300-address paste
               went out on the Save click with nothing in between. One address keeps going
               straight through — it is the pre-existing single invite, not a bulk send.

               UBQ-5507: only when Send Invite is on. The dialog exists solely to warn about
               email deliverability before a batch of emails goes out; with the flag off
               nobody is emailed, so it asked "You are about to send to N users" over a
               Send button that sends nothing. */
            if (mode === MODE.ADD && emails.length > 1 && sendInvitation) {
                setConfirmBulkOpen(true)
                return
            }
            void submitForm()
        },
        [validateForm, mode, emails.length, sendInvitation, submitForm],
    )

    const confirmBulkSend = useCallback(() => {
        setConfirmBulkOpen(false)
        void submitForm()
    }, [submitForm])

    return (
        <div>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <PaperModalComponent>
                    <form onSubmit={handleSubmit}>
                        <Box
                            sx={{
                                p: 4,
                                boxShadow: 4,
                                borderRadius: 4,
                                overflowY: 'auto',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '80%',
                                maxWidth: '1200px',
                                bgcolor: 'background.paper',
                                border: 2,
                                borderColor: 'primary.main',
                                maxHeight: '90vh',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '15px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon
                                    onClick={() => handleAddOrEditUser(false)}
                                    sx={{ marginRight: '8px' }}
                                    fontSize="small"
                                    id="modal-close-icon"
                                >
                                    <CloseOutlined
                                        sx={{
                                            color: '#000',
                                            marginTop: '-2px',
                                        }}
                                    />
                                </Icon>
                            </Box>
                            <Typography
                                variant="h6"
                                component="h2"
                                fontSize={18}
                                id="draggable-modal-title"
                                sx={{ cursor: 'move' }}
                            >
                                {titleText}
                            </Typography>
                            <Box sx={{ mt: 2, mb: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 2, fontSize: '18px' }}
                                >
                                    {t('userDetails')}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        // UBQ-5212: in ADD the email field holds a chip per invitee
                                        // and needs the full width, so org + role share the row above it.
                                        gridTemplateColumns:
                                            mode === MODE.ADD
                                                ? 'repeat(2, 1fr)'
                                                : 'repeat(3, 1fr)',
                                        gap: '16px',
                                    }}
                                >
                                    <TextField
                                        id="selected-org"
                                        label={t('selectedOrg')}
                                        variant="outlined"
                                        disabled
                                        value={org?.name}
                                        fullWidth
                                    />
                                    {mode === MODE.EDIT && (
                                        <TextField
                                            id="add-user-email"
                                            name="email"
                                            label={t('userEmailRequired')}
                                            variant="outlined"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            fullWidth
                                            disabled
                                            onBlur={handleEmailBlur}
                                            onKeyDown={handleKeyDown}
                                            InputProps={{
                                                endAdornment: null,
                                            }}
                                        />
                                    )}
                                    <FormControl
                                        fullWidth
                                        error={!!errors.role}
                                        disabled={readOnly}
                                    >
                                        <InputLabel>
                                            {t('userRoleRequired')}
                                        </InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="add-user-role"
                                            name="role"
                                            value={formData.role}
                                            label={t('userRoleRequired')}
                                            onChange={handleChange}
                                            disabled={
                                                totalUsers === 0 ||
                                                (totalUsers === 1 &&
                                                    mode === MODE.EDIT) ||
                                                readOnly
                                            }
                                        >
                                            {userRolesList?.map((i) => (
                                                <MenuItem
                                                    key={i.role}
                                                    value={i.role}
                                                    id={i.role}
                                                    disabled={i.disabled}
                                                    sx={{
                                                        display: i.disabled
                                                            ? 'none'
                                                            : 'flex',
                                                    }}
                                                >
                                                    {t(i.role)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.role && (
                                            <Typography
                                                variant="caption"
                                                sx={{ ml: '14px', mt: '3px' }}
                                                color="error"
                                            >
                                                {errors.role}
                                            </Typography>
                                        )}
                                    </FormControl>
                                    {mode === MODE.ADD && (
                                        <Box sx={{ gridColumn: '1 / -1' }}>
                                            {/* UBQ-5212: several people in one go. Keeps the
                                                `add-user-email` id — the E2E suite fills it by id. No
                                                `required`: the label already carries the asterisk. */}
                                            <MultiEmailField
                                                inputId="add-user-email"
                                                label={t('userEmailRequired')}
                                                value={emails}
                                                onChange={handleEmailsChange}
                                                disabled={readOnly}
                                                errorMessage={errors.email}
                                                verdicts={verdicts}
                                                blockingVerdicts={
                                                    BLOCKING_VERDICTS
                                                }
                                                placeholder={t(
                                                    'inviteEmailsPlaceholder',
                                                    {
                                                        max: MAX_USERS_PER_BATCH,
                                                    },
                                                )}
                                            />
                                            <Box sx={{ mt: 1 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            id="add-user-send-invitation"
                                                            checked={
                                                                sendInvitation
                                                            }
                                                            onChange={(e) =>
                                                                setSendInvitation(
                                                                    e.target
                                                                        .checked,
                                                                )
                                                            }
                                                            disabled={readOnly}
                                                        />
                                                    }
                                                    label={t(
                                                        'sendInvitationNow',
                                                    )}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    /* UBQ-5543: 12px is the standard
                                                       helper-text size; caption alone
                                                       renders 10.5px against the 14px
                                                       theme base. */
                                                    sx={{
                                                        opacity: 0.7,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {t('sendInvitationNowHelp')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ mb: 4 }}>
                                <LocalizationPreferences
                                    initialPreferences={localization}
                                    handleLocalizationChange={
                                        handleLocalizationChange
                                    }
                                    disabled={readOnly}
                                />
                            </Box>
                            <Box>
                                <UnitsPreferences
                                    units={units}
                                    onChangeUnits={onChangeUnits}
                                    disabled={readOnly}
                                />
                            </Box>
                            <Button
                                sx={{
                                    mt: 4,
                                    maxWidth: '200px',
                                    alignSelf: 'center',
                                }}
                                variant="contained"
                                id="add-user-submit-button"
                                /* UBQ-5212: deliberately NOT disabled on an empty or
                                   blocked address list. Disabling it there made
                                   validateForm's `emailRequired` and
                                   `inviteBlockedAddresses` messages unreachable — the
                                   form just sat there with a dead button and no
                                   explanation. Blocked addresses are already flagged red
                                   on the chips; clicking Submit now says why. */
                                disabled={
                                    (!isUpdated &&
                                        !isUnitsUpdated &&
                                        !isLocalizationUpdated) ||
                                    isFetchingUser ||
                                    readOnly
                                }
                                type="submit"
                            >
                                {t('submit')}
                            </Button>
                        </Box>
                    </form>
                </PaperModalComponent>
            </Modal>
            {/* UBQ-5212: the mandated preview before a batch goes out — counts plus the
                deliverability warning. Outside the form so submitting it cannot re-enter handleSubmit. */}
            {confirmBulkOpen && (
                <ConfirmationDialog
                    openDialog={confirmBulkOpen}
                    DialogHeaderText={t('bulkInviteTitle')}
                    DialogBodyText={
                        <BulkSendWarning
                            count={emails.length}
                            accountName={org?.name?.trim()}
                            showContinuePrompt
                        />
                    }
                    confirmtext={t('bulkResendConfirmAction')}
                    confirmId="bulk-invite-confirm-button"
                    cancelId="bulk-invite-cancel-button"
                    handleComfirm={confirmBulkSend}
                    handleClose={() => setConfirmBulkOpen(false)}
                />
            )}
        </div>
    )
}
